import { ReactNode, useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { CircularProgress, Flex } from '@tempmee/react';
import { ShiftDetailsPageRedesign } from '~/constants/ff.posthog';
import { useGetJob } from '~/domain/Jobs';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { DetailsPageFrame } from '~/components/shared/DetailsPage';
import { ShiftDetailsPageHoldProvider } from './shiftDetailsPageHold';
import {
  SHIFT_DETAILS_PAGES,
  shiftDetailsPageForStatus,
  type TShiftDetailsPageKey,
} from './shiftDetailsPages';

interface IShiftDetailsRedesignGateProps {
  /** The legacy screen for this route, rendered whenever the redesign does not take over. */
  children: ReactNode;
}

/**
 * Picks the redesigned page for the shift's status, falling back to the legacy
 * screen. Only mounted once the flag is on and at least one page is registered,
 * so the extra job fetch never happens on the legacy path.
 */
function ShiftDetailsRedesignResolver({ children }: IShiftDetailsRedesignGateProps) {
  const { jobId } = useParams<{ jobId?: string }>();
  const { data: job, isLoading } = useGetJob(jobId);

  // Which page has asked to keep its slot, and for which shift. Scoped to the
  // shift so a hold can never follow the office to a different one: navigating
  // away mid-popup would otherwise pin the new shift to the old shift's page.
  const [held, setHeld] = useState<{ key: TShiftDetailsPageKey; jobId?: string } | null>(null);

  // The status is the source of truth; a hold only defers acting on a change to
  // it, so the page an office is talking to does not vanish underneath them.
  const statusKey = job ? shiftDetailsPageForStatus(job.status) : null;
  const pageKey = held && held.jobId === jobId ? held.key : statusKey;

  // Bound to the page being rendered right now, so a page never has to name
  // itself — and a hold taken during this render pins exactly this page.
  const holdPage = useCallback(
    (isHeld: boolean) => setHeld(isHeld && pageKey ? { key: pageKey, jobId } : null),
    [jobId, pageKey],
  );

  if (isLoading) {
    return (
      <DetailsPageFrame testID="shift-details-loading">
        {/*
         * Inside the page's own frame, so the spinner centres against the same
         * container the content lands in and sits on the same background —
         * otherwise the page visibly jumps when the job arrives. `w-full` because
         * a bare `Flex` shrinks to its content when its parent lays out as a row,
         * which leaves "centred" wherever the spinner happens to sit.
         */}
        <Flex justify="center" py={10} className="w-full">
          <CircularProgress />
        </Flex>
      </DetailsPageFrame>
    );
  }

  const Page = pageKey ? SHIFT_DETAILS_PAGES[pageKey] : undefined;

  if (!job || !Page) {
    return <>{children}</>;
  }

  return (
    <ShiftDetailsPageHoldProvider value={holdPage}>
      <Page job={job} />
    </ShiftDetailsPageHoldProvider>
  );
}

/**
 * Feature gate for the Shift Details redesign. Wraps a legacy route element
 * rather than replacing it, so with the flag off the office hits exactly
 * today's screen and `OpenJobScreenSwitch`, `JobDetails` and `ConfirmCandidate`
 * stay untouched.
 *
 * The registry is empty until the per-state pages land, and an empty registry
 * short-circuits here — so turning the flag on today changes nothing, and each
 * page ticket activates its own status by registering itself.
 */
export function ShiftDetailsRedesignGate({ children }: IShiftDetailsRedesignGateProps) {
  const isRedesignEnabled = useFeatureFlag(ShiftDetailsPageRedesign);
  const hasRegisteredPages = Object.keys(SHIFT_DETAILS_PAGES).length > 0;

  if (!isRedesignEnabled || !hasRegisteredPages) {
    return <>{children}</>;
  }

  return <ShiftDetailsRedesignResolver>{children}</ShiftDetailsRedesignResolver>;
}
